import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, Loader2, AlertCircle, Download, Copy, Check, ExternalLink, Camera, Trash2, UserCircle2 } from "lucide-react";

/**
 * v23.2 — Driver verify badge.
 * v23.24 — Adds profile photo upload so the ID card feels like a real staff card.
 * Renders a printable/shareable QR that a customer can scan at the door to
 * confirm the driver is a verified Raanah operator. Backed by the public
 * GET /api/verify-driver/{token} endpoint.
 */
export default function DriverVerifyBadge() {
  const [qr, setQr] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  const [photoActing, setPhotoActing] = React.useState(false);
  const fileRef = React.useRef(null);

  const load = React.useCallback(async () => {
    try {
      const { data } = await apiClient.get("/driver/verify-qr");
      setQr(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load verify badge");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const copyUrl = async () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    try { await navigator.clipboard.writeText(qr.verify_url); } catch { /* clipboard locked down */ }
  };

  const onPickPhoto = () => fileRef.current?.click();

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so picking the same file again re-triggers
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Only image files can be used as a profile photo");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Photo too large. Max 5 MB.");
      return;
    }
    setPhotoActing(true); setError("");
    try {
      // Read as base64 and POST it. Cloudinary auto-crops to a 512×512 face-centred square.
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1]);
        r.onerror = () => reject(new Error("Couldn't read image"));
        r.readAsDataURL(file);
      });
      await apiClient.post("/driver/profile-photo", { file_b64: b64, file_mime: file.type });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Photo upload failed");
    } finally { setPhotoActing(false); }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove your profile photo from the ID card?")) return;
    setPhotoActing(true); setError("");
    try {
      await apiClient.delete("/driver/profile-photo");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't remove photo");
    } finally { setPhotoActing(false); }
  };

  return (
    <section className="mt-16" data-testid="driver-verify-badge-section">
      <div className="flex items-start gap-3 mb-4">
        <ShieldCheck className="text-[#1E4D55] mt-1" size={22} />
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A]">Your verify badge</h2>
          <p className="text-sm text-[#475569] max-w-xl mt-1">
            Print this QR or pin it inside your van. Customers scan it on the doorstep to confirm
            you&apos;re a checked Raanah driver — instant trust, fewer awkward intros.
          </p>
        </div>
      </div>

      {loading && (
        <div className="bg-white border border-[#0A0A0A]/10 p-6 inline-flex items-center gap-2 text-sm text-[#475569]" data-testid="driver-verify-loading">
          <Loader2 size={16} className="animate-spin" /> Building your badge&hellip;
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="driver-verify-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {qr && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-[#0A0A0A]/10" data-testid="driver-verify-card">
          {/* Photo + QR panel */}
          <div className="bg-white border-r border-b border-[#0A0A0A]/10 p-6 flex flex-col items-center">
            {/* Profile photo (or placeholder) */}
            <div className="mb-4 relative" data-testid="driver-photo-wrapper">
              {qr.profile_photo_url ? (
                <img
                  src={qr.profile_photo_url}
                  alt="Your Raanah ID photo"
                  data-testid="driver-profile-photo"
                  className="w-24 h-24 object-cover border-2 border-[#1E4D55]"
                />
              ) : (
                <div className="w-24 h-24 bg-[#F5F5F5] border-2 border-dashed border-[#0A0A0A]/20 flex items-center justify-center text-[#94a3b8]" data-testid="driver-photo-placeholder">
                  <UserCircle2 size={40} />
                </div>
              )}
              {photoActing && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                  <Loader2 size={22} className="animate-spin text-[#1E4D55]" />
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onPhotoChange}
              className="hidden"
              data-testid="driver-photo-input"
            />
            <img
              src={`data:image/png;base64,${qr.png_b64}`}
              alt="Your Raanah verify QR"
              data-testid="driver-verify-qr-image"
              className="w-full max-w-[200px]"
            />
            <p className="mt-3 text-[10px] font-mono text-[#475569] break-all text-center">{qr.verify_url}</p>
          </div>

          {/* Actions panel */}
          <div className="bg-white border-r border-b border-[#0A0A0A]/10 p-6 md:col-span-2">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">Customer-scannable proof</p>
            <h3 className="font-display mt-2 text-xl font-bold tracking-tight text-[#0A0A0A]">
              Verified by Raanah, at the door.
            </h3>
            <p className="mt-2 text-sm text-[#475569]">
              When scanned, customers see your masked name (e.g. &quot;John S.&quot;), a green &quot;Verified by Raanah&quot; badge,
              and the list of approved documents on file — no contact info, no doc files shared.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onPickPhoto}
                disabled={photoActing}
                data-testid="driver-photo-upload-button"
                className="inline-flex items-center gap-2 bg-white border border-[#1E4D55] text-[#1E4D55] hover:bg-[#1E4D55] hover:text-white transition-colors px-4 py-2 text-sm font-semibold disabled:opacity-60"
              >
                <Camera size={14} /> {qr.profile_photo_url ? "Replace photo" : "Upload photo"}
              </button>
              {qr.profile_photo_url && (
                <button
                  type="button"
                  onClick={removePhoto}
                  disabled={photoActing}
                  data-testid="driver-photo-remove-button"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#B91C1C] hover:text-[#7f1d1d] px-3 py-2 disabled:opacity-60"
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
              <a
                href={`data:image/png;base64,${qr.png_b64}`}
                download="raanah-verify-badge.png"
                data-testid="driver-verify-download"
                className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-sm font-semibold"
              >
                <Download size={14} /> Download PNG
              </a>
              <button
                onClick={copyUrl}
                data-testid="driver-verify-copy"
                className="inline-flex items-center gap-2 bg-white border border-[#1E4D55] text-[#1E4D55] hover:bg-[#1E4D55] hover:text-white transition-colors px-4 py-2 text-sm font-semibold"
              >
                {copied ? <><Check size={14}/> Copied!</> : <><Copy size={14}/> Copy link</>}
              </button>
              <a
                href={qr.verify_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="driver-verify-preview"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#475569] hover:text-[#0A0A0A] px-4 py-2"
              >
                <ExternalLink size={14} /> Preview as customer
              </a>
            </div>

            <p className="mt-4 text-[11px] text-[#475569]">
              Verify code: <span className="font-mono text-[#0A0A0A]">{qr.verify_token}</span> · Photo is auto-cropped to 512×512 face-centred. Max 5 MB.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
